import { QUERY_KEY_MY_MONITORING_GET_ACCOUNT, useMyMonitoringGetAccount } from '@apps-common/apis/my-monitoring/account/get-account'
import { useMyMonitoringUpdateAccount } from '@apps-common/apis/my-monitoring/account/update-account'
import { ImageComponent } from '@apps-common/components/common'
import { ProfileUserInitials } from '@apps-common/components/common/user'
import { ButtonRoundedSm, ImageSelect, InputSM } from '@apps-common/components/elements'
import { VertSpacerLg } from '@apps-common/components/layout'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { useAuth } from '@apps-common/contexts/AuthContext'
import { useCombinedUserDetails } from '@apps-common/hooks/useCombinedUserDetails'
import { queryClient } from '@apps-common/lib'
import { IMMAccountModel } from '@apps-common/types/my-monitoring/account'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

export const ProfileModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { reloadUser, updateProfileImage } = useAuth()
  const user = useCombinedUserDetails()
  const [file, setFile] = useState<File | null>()

  const { data, isLoading, isError } = useMyMonitoringGetAccount({})
  const { mutate, isPending } = useMyMonitoringUpdateAccount(true, async (data) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_ACCOUNT() })
    await reloadUser()
    updateProfileImage(data.profileImage)
    onDismiss(null, ModalDismiss.Confirm)
  })

  const {
    register,
    trigger,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm<IMMAccountModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onSubmit = async (values: IMMAccountModel) => mutate({ model: values, image: file })

  useEffect(() => {
    if (data) {
      reset(data)
    }
  }, [data])

  const loading = !data || isLoading || isError
  const updating = isPending || isSubmitting

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0 sm:!max-w-[450px]'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between pl-2'>
          <ModalHeader title='Account' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[70px]'>
        <div className='text-text'>
          <div className='flex items-center justify-between gap-6 mx-auto bg-surface border border-border rounded-full'>
            {loading && <div className='w-[80px] h-[80px] min-w-[80px] min-h-[80px] rounded-full bg-skeleton animate-pulse' />}
            {!loading && (
              <ImageSelect
                onChangeCallback={(imageFile: any) => {
                  setFile(imageFile)
                }}
                onClearCallback={(imageFile: any) => {}}
                cropDimensions={{
                  width: 200,
                  height: 200
                }}
                outerClasses='h-[85px]'
                containerClasses='bg-skeleton rounded-full overflow-hidden w-[85px] h-[85px] border border-border shadow-md'
                selectedImageClasses='w-[85px] h-[85px] min-w-[85px] min-h-[85px] rounded-full '
                clearChildren={
                  <div className='absolute top-0 right-0 bg-surface rounded-full p-[1px]'>
                    <commonIcons.close className='text-text-muted' />
                  </div>
                }
                disabled={loading || updating}
              >
                <div className='absolute top-0 right-0 bg-surface rounded-full p-[1px]'>
                  <commonIcons.editCircle className='text-text-muted' />
                </div>
                <ImageComponent
                  src={data?.profileImage}
                  errorComponent={
                    <ProfileUserInitials
                      name={user?.fullName || user?.displayName || user?.firstName}
                      classes='!w-full !h-full'
                      textClasses='!text-[1.6rem] capitalize'
                    />
                  }
                  alt='Profile image'
                  imageClasses='w-full h-full'
                  errorIconClasses='w-full h-full'
                />
              </ImageSelect>
            )}
            {!loading && (
              <div className='grow flex flex-col items-start justify-center gap-1'>
                <div className='jost-light'>
                  {data?.firstName} {data?.lastName}
                </div>
                <div className='jost-light text-sm opacity-75'>{data?.email}</div>
                <div className='jost text-xs opacity-75'>{data?.userName}</div>
              </div>
            )}
            {loading && (
              <div className='grow flex flex-col items-start justify-center gap-1 animate-pulse'>
                <div className='min-w-[70%] h-[24px] bg-skeleton rounded-lg' />
                <div className='border-b border-skeleton min-w-[70%]' />
                <div className='min-w-[85%] h-[20px] bg-skeleton rounded-lg' />
                <div className='min-w-[80%] h-[16px] bg-skeleton rounded-lg' />
              </div>
            )}
          </div>
          <VertSpacerLg />
          <div className='w-full border-b border-border' />
          <VertSpacerLg />
          <form id='updateProfileForm' noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className='flex items-center justify-between gap-2 '>
              <InputSM
                type='text'
                id='firstName'
                label='First Name'
                placeholder='Enter your first name'
                showLabel
                loading={loading}
                disabled={updating}
                registration={register('firstName', { required: FORM_DEFAULT_REQUIRED })}
                containerClassName='grow'
                errors={errors.firstName}
              />
              <InputSM
                type='text'
                id='lastName'
                label='Last Name'
                placeholder='Enter your last name'
                showLabel
                loading={loading}
                disabled={updating}
                registration={register('lastName', { required: FORM_DEFAULT_REQUIRED })}
                containerClassName='grow'
                errors={errors.lastName}
              />
            </div>
            <InputSM
              type='text'
              id='phone'
              label='Phone'
              placeholder='Enter your phone number'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('phoneNumber', {
                required: false,
                pattern: {
                  value: PHONE_VALIDATION_REGEX,
                  message: 'Invalid phone format'
                }
              })}
              errors={errors.lastName}
            />
          </form>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-4'>
          <ButtonRoundedSm type='submit' form='updateProfileForm' loading={loading || updating} text='Update' className='action-success' />
          <ButtonRoundedSm
            type='button'
            loading={loading || updating}
            text='Cancel'
            className='action-neutral'
            callback={() => onDismiss(null, ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
